import React from "react"
import { useLazyQuery, useQuery } from "@apollo/client"
import { gql } from "~/__generated__"
import { VoicePlaylistRequestStatusEnum } from "~/__generated__/graphql"
import { formatDistanceToNow } from "date-fns"
import { Button } from "~/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "~/ui/card"
import { Badge } from "~/ui/badge"
import { ScrollArea } from "~/ui/scroll-area"
import { Input } from "~/ui/input"
import { Label } from "~/ui/label"
import { Textarea } from "~/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/ui/select"
import { cn } from "~/lib/utils"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { useToast } from "~/hooks/use-toast"
import { TrackModal } from "~/tracks/track-modal"
import {
  AlertCircle,
  CheckCircle2,
  ClipboardList,
  Loader2,
  PlusCircle,
  RefreshCcw,
  Search,
} from "lucide-react"

const VOICE_PLAYLIST_REQUESTS_QUERY = gql(`
  query VoicePlaylistRequests($status: VoicePlaylistRequestStatusEnum, $first: Int, $after: String) {
    voicePlaylistRequests(status: $status, first: $first, after: $after) {
      edges {
        node {
          id
          requestType
          targetKind
          targetName
          artistName
          status
          errorMessage
          requestedMetadata
          resolutionPayload
          createdAt
          playlist {
            id
            name
          }
          voiceCommandEvent {
            id
            rawText
            normalizedText
            intent
            action
            success
            locale
            confidence
            createdAt
            user {
              id
              fullName
              email
            }
            location {
              id
              name
            }
            zone {
              id
              name
            }
          }
        }
      }
    }
  }
`)

const PLAYLISTS_QUERY = gql(`
  query VoicePlaylistRequestPlaylists($first: Int, $after: String) {
    playlists(first: $first, after: $after) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`)

const TRACKS_QUERY = gql(`
  query VoicePlaylistRequestTracks($query: String, $first: Int, $after: String) {
    tracks(query: $query, first: $first, after: $after) {
      edges {
        node {
          id
          name
          displayArtist
          playable
          album {
            id
            name
          }
        }
      }
    }
  }
`)

const RESOLVE_REQUEST_MUTATION = gql(`
  mutation ResolveVoicePlaylistRequest($input: ResolveVoicePlaylistRequestInput!) {
    resolveVoicePlaylistRequest(input: $input) {
      voicePlaylistRequest {
        id
        status
        resolutionPayload
        playlist {
          id
          name
        }
      }
      errors
    }
  }
`)

const ADD_TRACK_TO_PLAYLIST_MUTATION = gql(`
  mutation VoicePlaylistRequestAddTrack($input: AddTrackToPlaylistInput!) {
    addTrackToPlaylist(input: $input) {
      playlistTrack {
        id
      }
      errors
    }
  }
`)

export const VoicePlaylistRequestsScreen = () => {
  const { toast } = useToast()
  const [selectedRequestId, setSelectedRequestId] = React.useState<string | null>(null)
  const [notes, setNotes] = React.useState<string>("")
  const [selectedPlaylistId, setSelectedPlaylistId] = React.useState<string>("")
  const [isTrackModalOpen, setIsTrackModalOpen] = React.useState(false)
  const [trackSearchTerm, setTrackSearchTerm] = React.useState("")
  const [trackResults, setTrackResults] = React.useState<Array<any>>([])
  const [hasSearched, setHasSearched] = React.useState(false)

  const { data, loading, error, refetch } = useQuery(VOICE_PLAYLIST_REQUESTS_QUERY, {
    variables: { status: VoicePlaylistRequestStatusEnum.NeedsReview, first: 50 },
    pollInterval: 15000,
  })

  const { data: playlistsData } = useQuery(PLAYLISTS_QUERY, { variables: { first: 200 } })
  const [fetchTracks, { loading: tracksLoading }] = useLazyQuery(TRACKS_QUERY)
  const [resolveRequest, { loading: resolving }] = useSafeMutation(RESOLVE_REQUEST_MUTATION)
  const [addTrackToPlaylist, { loading: addingTrack }] = useSafeMutation(
    ADD_TRACK_TO_PLAYLIST_MUTATION,
  )

  const requests = React.useMemo(() => {
    const edges = data?.voicePlaylistRequests?.edges ?? []
    return edges.filter((edge): edge is { node: any } => Boolean(edge?.node))
  }, [data])

  const selectedRequest = React.useMemo(() => {
    return requests.find((edge) => edge.node.id === selectedRequestId)?.node ?? null
  }, [requests, selectedRequestId])

  React.useEffect(() => {
    if (!selectedRequestId && requests.length > 0) {
      setSelectedRequestId(requests[0].node.id)
    }
  }, [requests, selectedRequestId])

  React.useEffect(() => {
    if (selectedRequest) {
      setNotes((selectedRequest.resolutionPayload?.notes as string) ?? "")
      setSelectedPlaylistId(selectedRequest.playlist?.id ?? "")
    }
  }, [selectedRequest])

  React.useEffect(() => {
    setTrackSearchTerm("")
    setTrackResults([])
    setHasSearched(false)
  }, [selectedRequestId])

  const handleTrackSearch = React.useCallback(async () => {
    if (!trackSearchTerm.trim()) {
      toast({ title: "Enter a search term", description: "Provide a song, artist, or album name." })
      return
    }

    const result = await fetchTracks({ variables: { query: trackSearchTerm, first: 15 } })
    const nodes =
      result.data?.tracks?.edges?.map((edge) => edge?.node).filter(Boolean) ?? ([] as Array<any>)
    setTrackResults(nodes)
    setHasSearched(true)
  }, [fetchTracks, toast, trackSearchTerm])

  const playlistOptions = React.useMemo(() => {
    return (
      playlistsData?.playlists?.edges?.map((edge) => edge?.node).filter(Boolean) ?? []
    ) as Array<{ id: string; name: string }>
  }, [playlistsData])

  const handleResolve = React.useCallback(async () => {
    if (!selectedRequest) return

      const result = await resolveRequest({
        variables: {
          input: {
            id: selectedRequest.id,
          status: VoicePlaylistRequestStatusEnum.Completed,
          playlistId: selectedPlaylistId || null,
          resolutionPayload: { notes },
        },
      },
      refetchQueries: [
        {
          query: VOICE_PLAYLIST_REQUESTS_QUERY,
          variables: { status: VoicePlaylistRequestStatusEnum.NeedsReview, first: 50 },
        },
      ],
    })

    const errors = result.data?.resolveVoicePlaylistRequest?.errors
    if (errors && errors.length > 0) {
      toast({ title: "Unable to update request", description: errors.join("\n"), variant: "destructive" })
      return
    }

    toast({
      title: "Request resolved",
      description: "The playlist request has been marked as completed.",
    })
  }, [notes, resolveRequest, selectedPlaylistId, selectedRequest, toast])

  const handleAddTrackToPlaylist = React.useCallback(
    async (trackId: string) => {
      if (!selectedRequest) return
      if (!selectedPlaylistId) {
        toast({
          title: "Select a playlist",
          description: "Choose a playlist before adding tracks.",
          variant: "destructive",
        })
        return
      }

      const addResult = await addTrackToPlaylist({
        variables: { input: { playlistId: selectedPlaylistId, trackId } },
      })

      const addErrors = addResult.data?.addTrackToPlaylist?.errors
      if (addErrors && addErrors.length > 0) {
        toast({
          title: "Unable to add track",
          description: addErrors.join("\n"),
          variant: "destructive",
        })
        return
      }

      const resolveResult = await resolveRequest({
        variables: {
          input: {
            id: selectedRequest.id,
            status: VoicePlaylistRequestStatusEnum.Completed,
            playlistId: selectedPlaylistId,
            trackIds: [trackId],
            resolutionPayload: { notes },
          },
        },
        refetchQueries: [
          {
            query: VOICE_PLAYLIST_REQUESTS_QUERY,
            variables: { status: VoicePlaylistRequestStatusEnum.NeedsReview, first: 50 },
          },
        ],
      })

      const resolveErrors = resolveResult.data?.resolveVoicePlaylistRequest?.errors
      if (resolveErrors && resolveErrors.length > 0) {
        toast({
          title: "Track added but request not updated",
          description: resolveErrors.join("\n"),
          variant: "destructive",
        })
        return
      }

      toast({
        title: "Track added",
        description: "The track has been added to the playlist and the request closed.",
      })
      refetch()
    },
    [
      addTrackToPlaylist,
      notes,
      refetch,
      resolveRequest,
      selectedPlaylistId,
      selectedRequest,
      toast,
    ],
  )

  const renderRequestList = () => {
    if (loading) {
      return (
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading requests...
        </div>
      )
    }

    if (error) {
      return (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-4 text-center">
          <AlertCircle className="h-6 w-6 text-destructive" />
          <div className="font-medium text-destructive">Failed to load requests</div>
          <div className="text-sm text-muted-foreground">{error.message}</div>
          <Button onClick={() => refetch()} variant="outline">
            <RefreshCcw className="mr-2 h-4 w-4" /> Retry
          </Button>
        </div>
      )
    }

    if (!requests.length) {
      return (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 p-4 text-center">
          <ClipboardList className="h-10 w-10 text-muted-foreground" />
          <div className="text-lg font-semibold">No review requests</div>
          <p className="text-sm text-muted-foreground">
            All voice playlist commands have been handled. New review items will appear here.
          </p>
        </div>
      )
    }

    return (
      <ScrollArea className="h-full">
        <div className="flex flex-col gap-2 p-4">
          {requests.map((edge) => {
            const request = edge.node
            const isActive = request.id === selectedRequestId
            const statusLabel =
              request.status === VoicePlaylistRequestStatusEnum.NeedsReview
                ? "Needs review"
                : "Completed"
            return (
              <button
                key={request.id}
                onClick={() => setSelectedRequestId(request.id)}
                className={cn(
                  "rounded-lg border p-3 text-left transition",
                  isActive
                    ? "border-primary bg-primary/10"
                    : "border-border hover:bg-muted",
                )}
              >
                <div className="flex items-center justify-between">
                  <div className="font-medium">{request.targetName}</div>
                  <Badge
                    variant={
                      request.status === VoicePlaylistRequestStatusEnum.NeedsReview
                        ? "destructive"
                        : "default"
                    }
                  >
                    {statusLabel}
                  </Badge>
                </div>
                <div className="mt-1 text-sm text-muted-foreground">
                  {request.requestType.replace(/_/g, " ")}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  Requested {formatDistanceToNow(new Date(request.createdAt))} ago
                </div>
              </button>
            )
          })}
        </div>
      </ScrollArea>
    )
  }

  return (
    <div className="flex h-screen flex-col">
      <div className="flex items-center justify-between border-b p-6">
        <div>
          <h1 className="text-2xl font-semibold">Voice Playlist Requests</h1>
          <p className="text-sm text-muted-foreground">
            Review voice assistant requests that could not be completed automatically.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => refetch()}>
            <RefreshCcw className="mr-2 h-4 w-4" /> Refresh
          </Button>
          <Button onClick={() => setIsTrackModalOpen(true)}>
            <PlusCircle className="mr-2 h-4 w-4" /> New track
          </Button>
        </div>
      </div>
      <div className="flex flex-1 overflow-hidden">
        <div className="w-80 border-r bg-background">
          {renderRequestList()}
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {selectedRequest ? (
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">
                  {selectedRequest.targetName}
                  {selectedRequest.artistName ? (
                    <span className="text-sm font-normal text-muted-foreground">
                      {" "}· {selectedRequest.artistName}
                    </span>
                  ) : null}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <section className="grid gap-3 md:grid-cols-2">
                  <div>
                    <Label className="text-xs uppercase text-muted-foreground">Request type</Label>
                    <div className="text-sm font-medium capitalize">
                      {selectedRequest.requestType.replace(/_/g, " ")}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase text-muted-foreground">Status</Label>
                    <div className="text-sm font-medium">
                      {selectedRequest.status === VoicePlaylistRequestStatusEnum.NeedsReview
                        ? "Needs review"
                        : "Completed"}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase text-muted-foreground">Requested</Label>
                    <div className="text-sm font-medium">
                      {formatDistanceToNow(new Date(selectedRequest.createdAt), { addSuffix: true })}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase text-muted-foreground">Requested by</Label>
                    <div className="text-sm font-medium">
                      {selectedRequest.voiceCommandEvent.user?.fullName ?? "Unknown"}
                      <span className="block text-xs text-muted-foreground">
                        {selectedRequest.voiceCommandEvent.user?.email ?? ""}
                      </span>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase text-muted-foreground">Voice command</Label>
                    <div className="text-sm text-muted-foreground">{selectedRequest.voiceCommandEvent.rawText}</div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase text-muted-foreground">Confidence</Label>
                    <div className="text-sm font-medium">
                      {selectedRequest.voiceCommandEvent.confidence != null
                        ? `${Math.round(selectedRequest.voiceCommandEvent.confidence * 100)}%`
                        : "—"}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase text-muted-foreground">Location</Label>
                    <div className="text-sm font-medium">
                      {selectedRequest.voiceCommandEvent.location?.name ?? "Unknown"}
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs uppercase text-muted-foreground">Zone</Label>
                    <div className="text-sm font-medium">
                      {selectedRequest.voiceCommandEvent.zone?.name ?? "Unknown"}
                    </div>
                  </div>
                </section>

                <section className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="playlist-select">Playlist</Label>
                    <Select
                      value={selectedPlaylistId || undefined}
                      onValueChange={setSelectedPlaylistId}
                    >
                      <SelectTrigger id="playlist-select">
                        <SelectValue placeholder="Select a playlist" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="">No playlist</SelectItem>
                        {playlistOptions.map((playlist) => (
                          <SelectItem key={playlist.id} value={playlist.id}>
                            {playlist.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notes">Reviewer notes</Label>
                    <Textarea
                      id="notes"
                      value={notes}
                      onChange={(event) => setNotes(event.target.value)}
                      placeholder="Add context about how you resolved this request..."
                    />
                  </div>
                </section>

                <section className="space-y-3">
                  <Label className="text-sm font-semibold">Search library</Label>
                  <div className="flex flex-col gap-2 md:flex-row md:items-center">
                    <Input
                      value={trackSearchTerm}
                      onChange={(event) => setTrackSearchTerm(event.target.value)}
                      placeholder="Search by track, artist, or album"
                    />
                    <div className="flex gap-2">
                      <Button onClick={handleTrackSearch} disabled={!trackSearchTerm.trim()}>
                        <Search className="mr-2 h-4 w-4" /> Search
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setTrackSearchTerm("")
                          setTrackResults([])
                          setHasSearched(false)
                        }}
                      >
                        Clear
                      </Button>
                    </div>
                  </div>
                  <div className="rounded-lg border">
                    <ScrollArea className="h-64">
                      <div className="divide-y">
                        {tracksLoading ? (
                          <div className="flex items-center justify-center gap-2 p-4 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" /> Searching tracks...
                          </div>
                        ) : hasSearched && trackResults.length === 0 ? (
                          <div className="p-6 text-center text-sm text-muted-foreground">
                            No tracks matched that search.
                          </div>
                        ) : trackResults.length ? (
                          trackResults.map((track) => {
                            return (
                              <div key={track.id} className="flex items-center justify-between gap-4 p-3">
                                <div>
                                  <div className="font-medium">{track.name}</div>
                                  <div className="text-sm text-muted-foreground">
                                    {track.displayArtist || "Unknown artist"}
                                    {track.album?.name ? ` • ${track.album.name}` : ""}
                                  </div>
                                </div>
                                <Button
                                  size="sm"
                                  onClick={() => handleAddTrackToPlaylist(track.id)}
                                  disabled={addingTrack || resolving}
                                >
                                  <CheckCircle2 className="mr-2 h-4 w-4" /> Add
                                </Button>
                              </div>
                            )
                          })
                        ) : (
                          <div className="p-6 text-center text-sm text-muted-foreground">
                            Use the search form to find tracks to add.
                          </div>
                        )}
                      </div>
                    </ScrollArea>
                  </div>
                </section>

                <div className="flex flex-wrap items-center gap-2">
                  <Button onClick={handleResolve} disabled={resolving}>
                    {resolving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
                    Mark as resolved
                  </Button>
                  <Button
                    variant="outline"
                    onClick={async () => {
                      if (!selectedRequest) return
                      const result = await resolveRequest({
                        variables: {
                          input: {
                            id: selectedRequest.id,
                            status: VoicePlaylistRequestStatusEnum.NeedsReview,
                            playlistId: selectedPlaylistId || null,
                            resolutionPayload: { notes },
                          },
                        },
                      })
                      const errors = result.data?.resolveVoicePlaylistRequest?.errors
                      if (errors && errors.length > 0) {
                        toast({
                          title: "Unable to save notes",
                          description: errors.join("\n"),
                          variant: "destructive",
                        })
                        return
                      }
                      toast({
                        title: "Progress saved",
                        description: "Notes updated while keeping the request in review.",
                      })
                    }}
                  >
                    Save notes
                  </Button>
                  {selectedRequest.errorMessage ? (
                    <Badge variant="destructive" title={selectedRequest.errorMessage}>
                      Error: {selectedRequest.errorMessage}
                    </Badge>
                  ) : null}
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
              <ClipboardList className="h-10 w-10 text-muted-foreground" />
              <div className="text-lg font-semibold">Select a request to review</div>
              <p className="text-sm text-muted-foreground">
                Choose a voice playlist request from the list to view its details and take action.
              </p>
            </div>
          )}
        </div>
      </div>

      <TrackModal
        isOpen={isTrackModalOpen}
        onClose={() => setIsTrackModalOpen(false)}
        onSaved={() => {
          setIsTrackModalOpen(false)
          if (selectedRequestId) {
            refetch()
          }
        }}
      />
    </div>
  )
}
